import { BarChart3 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { analyticsApi, type AnalyticsApi } from '../../api/analytics';
import type { AnalyticsSite } from '../../api/types';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Spinner } from '../../components/Spinner';
import { useCapabilities } from '../../lib/capabilities';
import { useLoad } from '../../lib/useLoad';
import { InstallManage, NewInstall } from './InstallManage';
import type { InstallSection, ManageLink } from './installForm';

/**
 * Website analytics' settings and nothing else — the dispatch dashboard's
 * My Website → Analytics.
 *
 * The reports are read on the Dashboard's Analytics tab, so this page is the
 * other half: the first install's creation, then Install and settings for
 * whichever install is picked. It is the same `InstallManage` the analytics
 * screen opens from Tools, so the two cannot come to disagree about what a
 * setting does; what differs is only the way out — here it goes to the
 * reports wherever the host keeps them (`onOpenReports`), and with no host to
 * ask there is none, because this page has nothing behind it to go back to.
 */
export function AnalyticsSettings({
  api = analyticsApi,
  manageLink = null,
  onOpenReports = null,
}: {
  api?: AnalyticsApi;
  /** `?manage=<section>&site=<id>` — the Widgets card's Manage, or the Dashboard's settings links. */
  manageLink?: ManageLink | null;
  onOpenReports?: (() => void) | null;
}) {
  const { me } = useCapabilities();
  const level = me?.access.features.analytics;
  // Permissive until /meta lands, as AnalyticsView is — the server decides.
  const mayManage = level === undefined || level === 'manage';

  const sites = useLoad(() => api.listAnalyticsSites(), []);
  const [siteId, setSiteId] = useState<number | undefined>();
  const [section, setSection] = useState<InstallSection | undefined>();
  const site: AnalyticsSite | undefined = useMemo(() => {
    const all = sites.data ?? [];
    return all.find((row) => row.id === siteId) ?? all[0];
  }, [sites.data, siteId]);

  // A link names an install the page cannot know exists until the list says
  // so — honoured once, on its first answer, and only for an install on it.
  const linkSpent = useRef(false);
  useEffect(() => {
    if (!manageLink || linkSpent.current || !sites.data) return;
    linkSpent.current = true;
    if (sites.data.some((row) => row.id === manageLink.siteId)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSiteId(manageLink.siteId);
      setSection(manageLink.section);
    }
  }, [manageLink, sites.data]);

  const created = (id: number) => {
    setSiteId(id);
    setSection('install');
    sites.reload();
  };

  if (sites.loading && !sites.data) {
    return (
      <div className="analytics">
        <Spinner />
      </div>
    );
  }
  if (sites.error) {
    return (
      <div className="analytics">
        <ErrorNote error={sites.error} />
      </div>
    );
  }
  if (!site) {
    return (
      <div className="analytics">
        {mayManage ? (
          <NewInstall api={api} onCreated={created} />
        ) : (
          <Note tone="info">No install has been set up yet. An administrator can add one.</Note>
        )}
      </div>
    );
  }
  return (
    <div className="analytics">
      {/* Keyed on the install: switching to another one starts its forms from
          ITS values, not from the edits made to the last one. */}
      <InstallManage
        key={site.id}
        api={api}
        site={site}
        sites={sites.data ?? []}
        mayManage={mayManage}
        initialSection={section}
        refreshing={sites.refreshing}
        onChanged={sites.reload}
        onSelect={(id) => {
          setSiteId(id || undefined);
          // The section a link named was about the install it named.
          setSection(undefined);
        }}
        onCreated={created}
        onClose={onOpenReports}
        closeLabel={
          <>
            <BarChart3 size={14} /> See the reports on the Dashboard
          </>
        }
      />
    </div>
  );
}
